/**
 * Configuración centralizada de permisos de rutas por rol.
 * Define qué rutas puede acceder cada rol del sistema.
 */

/**
 * Ojo: esta union convive con `PlatformRole` de `utils/zod/common.ts`, que es la
 * que sale del token. Son dos declaraciones del mismo concepto, unidas por un
 * cast en `useNavbar.ts`. Agregar un rol a una sola compila limpio y falla en
 * runtime, asi que van siempre juntas.
 */
export type UserRole = "SUPERADMIN" | "MANAGER" | "STAFF" | "TRAINER";

/**
 * Rutas permitidas por rol.
 * Cada rol tiene acceso a las rutas listadas aquí.
 */
export const ROUTE_PERMISSIONS: Record<UserRole, string[]> = {
  SUPERADMIN: [
    "/profile",
    // Ayuda (solo development, ver App.tsx)
    "/help",
    "/admin",
    "/admin/gyms",
    "/admin/gym", // Para /admin/gym/:publicId
    "/admin/users",
    "/admin/new-gym-guide",
    "/admin/plans",
    "/admin/subscriptions",
    "/admin/gateway-transactions",
    "/admin/mail-tester",
    "/admin/mail-usage",
    "/admin/app-banners",
    "/admin/app-releases",
    "/admin/legal",
    "/monitoring",
  ],
  MANAGER: [
    "/profile",
    // Ayuda (solo development, ver App.tsx)
    "/help",
    // Operaciones
    "/",
    "/access-control",
    "/plan-payments",
    "/food-sales",
    "/merchandise-sales",
    // Gestión
    "/clients",
    "/client", // Para /client/:publicId
    "/subscriptions",
    "/bookings",
    "/trainers",
    "/trainer", // Para /trainer/:publicId
    "/shifts",
    // Mi Gym
    "/plans",
    "/foods",
    "/merchandise",
    "/inventory",
    "/machinery",
    "/suppliers",
    // Finanzas
    "/expenses",
    "/expenses-history",
    "/other-incomes",
    "/metrics",
    "/unified-incomes",
    "/gateway-transactions",
    "/gateway-subscriptions",
    // B2B
    "/sales-orders",
    "/invoices",
    "/materials",
    "/products",
    "/production-metrics",
    "/b2b-crm",
    // Campanas
    "/campaigns",
    // App del socio
    "/app-banners",
    // Importaciones
    "/import/clients",
    "/import/plans",
    "/import/active-subscriptions",
    // Cobro Dashcore (solo development, ver App.tsx)
    "/billing",
  ],
  STAFF: [
    "/profile",
    // Ayuda (solo development, ver App.tsx)
    "/help",
    // Operaciones
    "/",
    "/access-control",
    "/plan-payments",
    "/food-sales",
    "/merchandise-sales",
    // Gestión
    "/clients",
    "/client", // Para /client/:publicId
    "/subscriptions",
    "/bookings",
    "/shifts",
    // Cobros recurrentes: solo crear (desde el perfil del cliente), sin gestión
    "/gateway-subscriptions/new",
    // Mi Gym (solo catálogos, sin inventario ni maquinaria)
    "/plans",
    "/foods",
    "/merchandise",
  ],

  // El entrenador no ve nada del panel. Su espacio propio es todo su alcance:
  // el prefijo cubre las subrutas, asi que /trainer-account/schedule ya entra.
  TRAINER: ["/trainer-account"],
};

/**
 * Verifica si un rol tiene acceso a una ruta específica.
 */
export const canAccessRoute = (
  role: UserRole | null,
  path: string,
): boolean => {
  if (!role) return false;

  const allowedRoutes = ROUTE_PERMISSIONS[role];
  if (!allowedRoutes) return false;

  // Verificar coincidencia exacta o si la ruta comienza con una permitida
  return allowedRoutes.some((route) => {
    if (route === path) return true;
    // Para rutas dinámicas como /client/:publicId
    if (path.startsWith(route + "/")) return true;
    return false;
  });
};

/**
 * Obtiene la ruta de inicio por defecto según el rol.
 */
export const getHomeRouteByRole = (role: UserRole | null): string => {
  switch (role) {
    case "SUPERADMIN":
      return "/profile";
    case "TRAINER":
      // Aterriza en la agenda, que es a lo que entra a mirar. `/trainer-account`
      // redirige acá; la bandeja vive en `/trainer-account/invitations`.
      return "/trainer-account/home";
    case "MANAGER":
    case "STAFF":
      return "/";
    default:
      return "/auth";
  }
};
